/**
 * API Client for IMC Graphs Dashboard
 *
 * Provides typed fetch wrapper with:
 * - Session cookie handling (credentials: 'include')
 * - Error class with status codes
 * - Request timeout
 * - JSON parsing with error recovery
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isServerError(): boolean {
    return this.status >= 500
  }
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  timeout?: number
  body?: unknown
}

/**
 * Type-safe fetch wrapper for API requests.
 *
 * - Automatically includes session cookies
 * - Handles JSON serialization/deserialization
 * - Provides typed error handling
 * - Supports request timeout
 *
 * @example
 * const runs = await fetchApi<Run[]>('/api/v1/runs')
 * const run = await fetchApi<Run>('/api/v1/runs/123')
 * const result = await fetchApi<void>('/api/v1/runs/123', { method: 'DELETE' })
 */
export async function fetchApi<T>(
  endpoint: string,
  options?: FetchOptions
): Promise<T> {
  const { timeout = 30000, body, ...fetchOptions } = options || {}

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(endpoint, {
      ...fetchOptions,
      signal: controller.signal,
      credentials: 'include', // CRITICAL: Include session cookies
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    })

    clearTimeout(timeoutId)

    // Handle non-JSON responses (204 No Content, etc.)
    const contentType = response.headers.get('content-type')
    const isJson = contentType?.includes('application/json')

    if (!response.ok) {
      let errorData: unknown
      let errorMessage = response.statusText

      if (isJson) {
        try {
          errorData = await response.json()
          errorMessage = (errorData as { error?: string; message?: string })?.error
            || (errorData as { message?: string })?.message
            || response.statusText
        } catch {
          // JSON parse failed, use status text
        }
      }

      throw new ApiError(response.status, errorMessage, errorData)
    }

    // Return empty for 204 No Content
    if (response.status === 204 || !isJson) {
      return undefined as T
    }

    return response.json() as Promise<T>
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof ApiError) {
      throw error
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(408, 'Request timeout')
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiError(0, 'Network error - server may be unavailable')
    }

    throw new ApiError(0, 'Unknown error', error)
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: Omit<FetchOptions, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: Omit<FetchOptions, 'method'>) =>
    fetchApi<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: Omit<FetchOptions, 'method'>) =>
    fetchApi<T>(endpoint, { ...options, method: 'PUT', body }),

  delete: <T>(endpoint: string, options?: Omit<FetchOptions, 'method' | 'body'>) =>
    fetchApi<T>(endpoint, { ...options, method: 'DELETE' }),
}
